import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import SidebarUserFooter from './SidebarUserFooter';
import { 
  Plus, 
  Search, 
  Filter, 
  BookOpen, 
  Heart, 
  Leaf, 
  Calendar, 
  DollarSign,
  Users,
  LogOut,
  Menu,
  X,
  BarChart3,
  Activity,
  Clock,
  CheckCircle2,
  AlertCircle,
  Trash2
} from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const Projects = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(() => sessionStorage.getItem('projects_search') || '');
  const [filterStatus, setFilterStatus] = useState(() => sessionStorage.getItem('projects_filterStatus') || 'all');
  const [filterCategory, setFilterCategory] = useState(() => sessionStorage.getItem('projects_filterCategory') || 'all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    start_date: '',
    end_date: '',
    budget: ''
  });

  useEffect(() => {
    fetchProjects();
  }, []);

  // Persist filter state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('projects_search', searchTerm);
  }, [searchTerm]);
  useEffect(() => {
    sessionStorage.setItem('projects_filterStatus', filterStatus);
  }, [filterStatus]);
  useEffect(() => {
    sessionStorage.setItem('projects_filterCategory', filterCategory);
  }, [filterCategory]);

  // Save scroll position when navigating away
  useEffect(() => {
    const saveScroll = () => sessionStorage.setItem('projects_scrollY', String(window.scrollY));
    window.addEventListener('beforeunload', saveScroll);
    return () => {
      saveScroll();
      window.removeEventListener('beforeunload', saveScroll);
    };
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error(t('projects_page.load_failed'));
    } finally {
      setLoading(false);
      // Restore scroll position after data loads
      requestAnimationFrame(() => {
        const savedScroll = sessionStorage.getItem('projects_scrollY');
        if (savedScroll) {
          window.scrollTo(0, parseInt(savedScroll, 10));
        }
      });
    }
  };

  const handleDeleteProject = async (project) => {
    if (user?.role !== 'administrator') return;
    const ok = window.confirm(
      `Permanently delete project "${project.title}"?\n\n` +
      `This will also delete all related tasks, deliverables, KPIs, expenses, payments, risks, issues, ` +
      `audit log entries and more. This cannot be undone.`,
    );
    if (!ok) return;
    try {
      const res = await axios.delete(`${API}/projects/${project.id}`);
      const cascade = res.data?.cascade || {};
      const collCount = Object.keys(cascade).length;
      toast.success(
        `Project deleted${collCount ? ` (also removed ${Object.values(cascade).reduce((a, b) => a + b, 0)} related records across ${collCount} collections)` : ''}`,
      );
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete project');
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    try {
      const projectData = {
        ...formData,
        budget: parseFloat(formData.budget),
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString()
      };

      await axios.post(`${API}/projects`, projectData);
      toast.success(t('projects_page.create_success'));
      setShowCreateDialog(false);
      setFormData({
        title: '',
        description: '',
        category: '',
        start_date: '',
        end_date: '',
        budget: ''
      });
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('projects_page.create_failed'));
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'education': return <BookOpen className="h-5 w-5" />;
      case 'care': return <Heart className="h-5 w-5" />;
      case 'environment': return <Leaf className="h-5 w-5" />;
      default: return <Activity className="h-5 w-5" />;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'education': return 'bg-blue-100 text-blue-800';
      case 'care': return 'bg-green-100 text-green-800';
      case 'environment': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'executing': return 'bg-green-100 text-green-800';
      case 'planning': return 'bg-yellow-100 text-yellow-800';
      case 'initiating': return 'bg-blue-100 text-blue-800';
      case 'closing': return 'bg-purple-100 text-purple-800';
      case 'on_hold': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { 
      style: 'currency', 
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || project.status === filterStatus;
    const matchesCategory = filterCategory === 'all' || project.category === filterCategory;
    
    return matchesSearch && matchesStatus && matchesCategory;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`fixed left-0 top-0 h-full w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out z-50 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-6 border-b">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-gray-800">ISISP Platform</h2>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
              aria-label="Close sidebar"
              data-testid="projects-sidebar-close-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-gray-600 mt-1">Social Impact Management</p>
        </div>

        <nav className="mt-6 px-4">
          <div className="space-y-2">
            <Link 
              to="/dashboard" 
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              <BarChart3 className="h-5 w-5" />
              <span>Dashboard</span>
            </Link>
            <Link 
              to="/projects" 
              className="flex items-center space-x-3 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 font-medium"
            >
              <Activity className="h-5 w-5" />
              <span>Projects</span>
            </Link>
            <Link 
              to="/templates" 
              className="flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              <BookOpen className="h-5 w-5" />
              <span>Electronic Library of Financial Instruments & Supporting Material</span>
            </Link>
          </div>
        </nav>

        <div className="absolute bottom-6 left-4 right-4">
          <SidebarUserFooter user={user} onLogout={onLogout} />
        </div>
      </div>

      {/* Main Content */}
      <div className="lg:ml-64">
        <header className="bg-white shadow-sm border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-gray-100 rounded-lg"
                aria-label="Open sidebar menu"
                data-testid="projects-sidebar-toggle"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('projects_page.title')}</h1>
                <p className="text-sm text-gray-600">{t('projects_page.subtitle')}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Link to="/templates">
                <Button variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                  <BookOpen className="h-4 w-4 mr-2" />
                  {t('projects_page.browse_library')}
                </Button>
              </Link>
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-gradient-to-r from-blue-600 to-indigo-600" data-testid="create-project-button">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('projects_page.create_project')}
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-2xl z-50">
                <DialogHeader>
                  <DialogTitle>{t('projects_page.create_new_project')}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateProject} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="title">{t('projects_page.field_title')}</Label>
                      <Input
                        id="title"
                        value={formData.title}
                        onChange={(e) => setFormData({...formData, title: e.target.value})}
                        placeholder={t('projects_page.field_title_placeholder')}
                        required
                        data-testid="project-title-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="category">{t('projects_page.field_category')}</Label>
                      <Select value={formData.category} onValueChange={(value) => setFormData({...formData, category: value})}>
                        <SelectTrigger data-testid="project-category-select" className="relative z-50">
                          <SelectValue placeholder={t('projects_page.select_category')} />
                        </SelectTrigger>
                        <SelectContent className="z-50">
                          <SelectItem value="education">{t('projects_page.category_education', 'Education')}</SelectItem>
                          <SelectItem value="care">{t('projects_page.category_care', 'Care')}</SelectItem>
                          <SelectItem value="environment">{t('projects_page.category_environment', 'Environment')}</SelectItem>
                          <SelectItem value="health">Health</SelectItem>
                          <SelectItem value="tourism">Tourism</SelectItem>
                          <SelectItem value="agriculture">Agriculture</SelectItem>
                          <SelectItem value="ict">Information &amp; Communication Technology</SelectItem>
                          <SelectItem value="entrepreneurship">Entrepreneurship</SelectItem>
                          <SelectItem value="training">Training</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="description">{t('projects_page.field_description')}</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder={t('projects_page.field_description_placeholder')}
                      rows={3}
                      required
                      data-testid="project-description-input"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="start_date">{t('projects_page.field_start_date')}</Label>
                      <Input
                        id="start_date"
                        type="date"
                        value={formData.start_date}
                        onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                        required
                        data-testid="project-start-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="end_date">{t('projects_page.field_end_date')}</Label>
                      <Input
                        id="end_date"
                        type="date"
                        value={formData.end_date}
                        onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                        required
                        data-testid="project-end-date-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="budget">{t('projects_page.field_budget')}</Label>
                      <Input
                        id="budget"
                        type="number"
                        value={formData.budget}
                        onChange={(e) => setFormData({...formData, budget: e.target.value})}
                        placeholder="0.00"
                        required
                        data-testid="project-budget-input"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end space-x-2 pt-4">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowCreateDialog(false)}
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button type="submit" data-testid="create-project-submit">
                      {t('projects_page.create_project')}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </header>

        <main className="p-6">
          {/* Filters */}
          <Card className="mb-6">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <label htmlFor="projects-search-input" className="sr-only">
                      {t('projects_page.search_placeholder')}
                    </label>
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" aria-hidden="true" />
                    <Input
                      id="projects-search-input"
                      placeholder={t('projects_page.search_placeholder')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-projects-input"
                    />
                  </div>
                </div>
                <div className="flex gap-4">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-40" data-testid="filter-status-select">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder={t('projects_page.filter_status_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('projects_page.all_status')}</SelectItem>
                      <SelectItem value="initiating">{t('projects_page.status_initiating')}</SelectItem>
                      <SelectItem value="planning">{t('projects_page.status_planning')}</SelectItem>
                      <SelectItem value="executing">{t('projects_page.status_executing')}</SelectItem>
                      <SelectItem value="closing">{t('projects_page.status_closing')}</SelectItem>
                      <SelectItem value="on_hold">{t('projects_page.status_on_hold')}</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger className="w-40" data-testid="filter-category-select">
                      <Filter className="h-4 w-4 mr-2" />
                      <SelectValue placeholder={t('projects_page.filter_category_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('projects_page.all_categories')}</SelectItem>
                      <SelectItem value="education">{t('projects_page.category_education', 'Education')}</SelectItem>
                      <SelectItem value="care">{t('projects_page.category_care', 'Care')}</SelectItem>
                      <SelectItem value="environment">{t('projects_page.category_environment', 'Environment')}</SelectItem>
                      <SelectItem value="health">Health</SelectItem>
                      <SelectItem value="tourism">Tourism</SelectItem>
                      <SelectItem value="agriculture">Agriculture</SelectItem>
                      <SelectItem value="ict">Information &amp; Communication Technology</SelectItem>
                      <SelectItem value="entrepreneurship">Entrepreneurship</SelectItem>
                      <SelectItem value="training">Training</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="hover-lift transition-all duration-300" data-testid={`project-card-${project.id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-2">
                      <div className="p-2 rounded-full bg-gray-100">
                        {getCategoryIcon(project.category)}
                      </div>
                      <div>
                        <Badge className={getCategoryColor(project.category)}>
                          {project.category}
                        </Badge>
                      </div>
                    </div>
                    <Badge className={getStatusColor(project.status)}>
                      {project.status.replace('_', ' ')}
                    </Badge>
                  </div>
                  <CardTitle className="text-lg leading-tight">
                    {/* Title intentionally rendered as plain text, not as a
                        separate <Link>. The card's only AT-discoverable link
                        is the "View details" button below — this avoids the
                        WAVE 'Redundant link' alert that fires when the title
                        and the CTA both point to the same destination. */}
                    <span data-testid={`project-title-${project.id}`}>{project.title}</span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                    {project.description}
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span
                        className="text-gray-500 cursor-help"
                        title={
                          project.progress_breakdown
                            ? Object.entries(project.progress_breakdown)
                                .map(([k, v]) => `${k.charAt(0).toUpperCase() + k.slice(1)}: ${v}%`)
                                .join(' · ')
                            : 'Auto-computed from deliverables and KPIs'
                        }
                        data-testid={`progress-tooltip-${project.id}`}
                      >
                        {t('projects_page.label_progress')}
                      </span>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">{Math.round(project.progress)}%</span>
                        {project.progress >= 75 ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : project.progress >= 25 ? (
                          <Clock className="h-4 w-4 text-yellow-700" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-600" />
                        )}
                      </div>
                    </div>
                    <Progress value={project.progress} />
                    
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center space-x-1">
                        <DollarSign className="h-4 w-4 text-green-700" />
                        <span className="text-gray-500">{t('projects_page.label_budget_inline')}</span>
                        <span className="font-medium">{formatCurrency(project.budget)}</span>
                      </div>
                      <div className="flex items-center space-x-1 text-gray-500">
                        <Calendar className="h-4 w-4" />
                        <span>{new Date(project.end_date).toLocaleDateString('en-GB')}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t flex gap-2">
                    <Link to={`/projects/${project.id}`} className="flex-1">
                      <Button variant="outline" className="w-full" data-testid={`view-project-button-${project.id}`}>
                        {t('projects_page.view_details')}
                      </Button>
                    </Link>
                    {user?.role === 'administrator' && (
                      <Button
                        variant="outline"
                        size="icon"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                        aria-label={`Delete project ${project.title}`}
                        title="Delete project (Admin only)"
                        onClick={() => handleDeleteProject(project)}
                        data-testid={`delete-project-button-${project.id}`}
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredProjects.length === 0 && (
            <div className="text-center py-12">
              <Activity className="h-16 w-16 mx-auto text-gray-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('projects_page.no_projects_found')}</h3>
              <p className="text-gray-500 mb-4">
                {projects.length === 0 
                  ? t('projects_page.no_projects_first_hint')
                  : t('projects_page.no_projects_filter_hint')}
              </p>
              {projects.length === 0 && (
                <Button onClick={() => setShowCreateDialog(true)} data-testid="empty-state-create-project">
                  <Plus className="h-4 w-4 mr-2" />
                  {t('projects_page.create_first_project')}
                </Button>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Projects;